import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext.jsx';
import { supabase } from './supabase.js';
import { acceptBiddingTerms, allowanceLabel, currentTermsVersion, getBidAllowance, requestBilling, stripeCheckoutUrl, subscriptionChoices } from './billing.js';
import { proposalReturnPath } from './proposalDrafts.js';
import { SignInGate } from './SignIn.jsx';

function resetLabel(value) {
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'long', timeZone: 'Europe/London' }).format(new Date(value));
}

export function BiddingTermsConsent() {
  const [accepted, setAccepted] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let current = true;
    supabase.rpc('has_accepted_current_terms', { policy_version: currentTermsVersion }).then(({ data, error: cause }) => {
      if (current) { if (cause) setError('Could not check your terms acceptance.'); else { setAccepted(data === true); setError(''); } }
    }).catch(() => { if (current) setError('Could not check your terms acceptance.'); });
    return () => { current = false; };
  }, [retry]);
  if (accepted) return null;
  return <section className="cloud-notice">
    {accepted === false && <><label><input type="checkbox" checked={checked} onChange={(event) => setChecked(event.target.checked)} /> I accept the <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>, including zero tolerance for abusive or objectionable content.</label>
      <button className="button button-secondary" type="button" disabled={!checked || busy} onClick={async () => {
        setBusy(true); setError('');
        try { await acceptBiddingTerms(supabase, checked); setAccepted(true); }
        catch (cause) { setError(cause.message); }
        finally { setBusy(false); }
      }}>Save acceptance</button></>}
    {error && <><p role="alert">{error}</p><button className="text-link" type="button" onClick={() => setRetry((value) => value + 1)}>Retry</button></>}
  </section>;
}

export function BidAllowancePanel({ companyId, refreshKey = 0 }) {
  const [state, setState] = useState({ companyId: null, allowance: null, error: '' });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let current = true;
    if (!companyId || !supabase) return () => { current = false; };
    getBidAllowance(supabase, companyId).then((allowance) => {
      if (current) setState({ companyId, allowance, error: '' });
    }).catch((cause) => {
      if (current) setState({ companyId, allowance: null, error: cause.message });
    });
    return () => { current = false; };
  }, [companyId, refreshKey, retry]);
  if (!companyId) return null;
  const allowance = state.companyId === companyId ? state.allowance : null;
  const error = state.companyId === companyId ? state.error : '';
  return <aside className="cloud-notice" aria-live="polite">
    {error ? <><p>Could not load your bid allowance. Your draft is safe.</p><button type="button" className="text-link" onClick={() => setRetry((value) => value + 1)}>Retry allowance</button></>
      : allowance ? <><strong>{allowanceLabel(allowance)}</strong><p>{allowance.subscribed ? 'Your company’s active subscription covers all its members.' : `Shared across your company. Free bids reset on ${resetLabel(allowance.resetsAt)} (London time).`}</p>
        <Link to={`/supplier/billing?company=${encodeURIComponent(companyId)}`}>{allowance.subscribed ? 'Manage subscription' : 'View bidding subscription'}</Link></>
        : <p>Loading your company’s allowance…</p>}
  </aside>;
}

export default function BillingPage() {
  const { user, loading } = useAuth();
  if (loading) return <div className="content-wrap"><p role="status">Loading your account…</p></div>;
  if (!user || !supabase) return <SignInGate next="/supplier/billing"/>;
  return <CompanyBilling key={user.id} userId={user.id} />;
}

function CompanyBilling({ userId }) {
  const [search] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const [companyRetry, setCompanyRetry] = useState(0);
  const preferredCompany = search.get('company');
  useEffect(() => {
    let current = true;
    (async () => {
      const { data, error: cause } = await supabase.from('organization_memberships')
        .select('organization_id,organizations!inner(id,name,kind)').eq('user_id', userId).eq('active', true).eq('organizations.kind', 'supplier');
      if (cause) throw cause;
      const rows = (data || []).flatMap((row) => {
        const company = Array.isArray(row.organizations) ? row.organizations[0] : row.organizations;
        return company ? [company] : [];
      });
      if (current) {
        setCompanies(rows);
        setCompanyId(rows.some((company) => company.id === preferredCompany) ? preferredCompany : rows[0]?.id || '');
        setLoading(false); setError('');
      }
    })().catch((cause) => { if (current) { setError(cause.message); setLoading(false); } });
    return () => { current = false; };
  }, [userId, preferredCompany, companyRetry]);
  useEffect(() => {
    let current = true;
    if (!companyId) return () => { current = false; };
    setStatus(null);
    requestBilling(supabase, 'subscription-status', { organizationId: companyId }).then((value) => {
      if (current) { setStatus({ ...value, organizationId: companyId }); setError(''); }
    }).catch((cause) => { if (current) setError(cause.message); });
    return () => { current = false; };
  }, [companyId, version]);
  const currentStatus = status?.organizationId === companyId ? status : null;
  const choices = subscriptionChoices(currentStatus);
  const openPortal = async () => {
    setBusy(true); setError('');
    try {
      const result = await requestBilling(supabase, 'create-subscription-portal', { organizationId: companyId });
      window.location.assign(stripeCheckoutUrl(result.url));
    } catch (cause) { setError(cause.message); }
    finally { setBusy(false); }
  };
  return <div className="content-wrap account-page">
    <header className="coord-page-heading"><div><span className="account-kicker">SUPPLIER BILLING</span><h1>Bid on more jobs.</h1><p>Five free bids per company each calendar month. For unlimited bidding, subscribe in the Resident Service 360 iPhone app. Resident Service 360 takes no commission on supplier job payments.</p></div></header>
    <p><Link to={proposalReturnPath(userId)}>Return to your proposal</Link></p>
    {loading ? <p role="status">Loading your companies…</p> : companies.length > 0 ? <>
      <label className="account-form">Company<select value={companyId} disabled={busy} onChange={(event) => { setCompanyId(event.target.value); setError(''); }}>{companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}</select></label>
      <BidAllowancePanel companyId={companyId} refreshKey={version} />
      <section className="account-content-panel">
        <h2>{currentStatus?.subscribed ? 'Your subscription is active' : 'Unlimited bidding'}</h2>
        {!currentStatus ? <p>Loading subscription status…</p> : currentStatus.subscribed ? <p>Existing bids and accepted jobs stay available if your subscription expires.</p>
          : <><p>Subscriptions are bought in the Resident Service 360 iPhone app. Open the app, go to Account, then Bidding subscription. Discount codes are redeemed there too.</p>
            <p>Your subscription covers everyone in your company, here and in the app.</p></>}
        {choices.manageApple && <a className="button button-secondary" href="https://apps.apple.com/account/subscriptions">Manage App Store subscription</a>}
        {choices.manageStripe && <button className="button button-blue" disabled={busy} onClick={() => void openPortal()}>Manage Stripe subscription</button>}
        {currentStatus?.subscribed && !currentStatus.canManage && <p>Ask a company owner or administrator to manage the subscription.</p>}
        <p><Link to="/terms">Terms</Link> · <Link to="/privacy">Privacy</Link></p>
      </section>
      <button type="button" className="text-link" disabled={busy} onClick={() => setVersion((value) => value + 1)}>Refresh subscription status</button>
      <p>After subscribing in the app, refresh to see your company’s verified access.</p>
    </> : !error && <p>Add a supplier company to manage bidding. <Link to="/supplier/opportunities">Open your supplier workspace</Link>.</p>}
    {error && <div role="alert"><p className="account-form-error">{error}</p><button type="button" className="text-link" onClick={() => companies.length ? setVersion((value) => value + 1) : setCompanyRetry((value) => value + 1)}>Retry</button></div>}
  </div>;
}
